import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefon',
      info: '0216 998 90 30',
      subInfo: 'Fax: 0216 693 16 90',
      link: 'tel:02169989030'
    },
    {
      icon: Mail,
      title: 'E-posta',
      info: 'ik@bdhadanismanlik.com',
      subInfo: 'Her zaman ulaşabilirsiniz',
      link: 'mailto:ik@bdhadanismanlik.com'
    },
    {
      icon: MapPin,
      title: 'Adres',
      info: 'Kavacık Mah. Fatih Sultan Mehmet Cad.',
      subInfo: 'Tonoğlu İş Merkezi No:3/4 Beykoz/İSTANBUL',
      link: 'https://maps.google.com/?q=Kavacık+Mah+Beykoz+İstanbul'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      info: 'Pazartesi - Cuma',
      subInfo: '09:00 - 18:00',
      link: null
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Mesajınız başarıyla gönderildi!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-br from-[#2A9D8F]/5 to-[#E76F51]/5">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm font-medium mb-6">
              İletişim
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#264653] mb-6 font-['Outfit']">
              Bizimle İletişime Geçin
            </h1>
            <p className="text-lg text-[#264653]/70 leading-relaxed">
              İnsan kaynakları ihtiyaçlarınız için size en uygun çözümü sunalım. 
              Sorularınız için bize ulaşın.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16" data-testid="contact-info-section">
        <div className="section-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white border border-[#264653]/5 hover:shadow-lg transition-shadow text-center"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-14 h-14 mx-auto bg-[#2A9D8F]/10 rounded-2xl flex items-center justify-center mb-4">
                  <item.icon className="text-[#2A9D8F]" size={24} />
                </div>
                <h3 className="font-semibold text-[#264653] mb-2 font-['Outfit']">{item.title}</h3>
                {item.link ? (
                  <a href={item.link} className="text-[#264653]/70 hover:text-[#2A9D8F] transition-colors" target={item.link.startsWith('http') ? '_blank' : undefined} rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}>
                    <p>{item.info}</p>
                    <p className="text-sm">{item.subInfo}</p>
                  </a>
                ) : (
                  <>
                    <p className="text-[#264653]/70">{item.info}</p>
                    <p className="text-[#264653]/70 text-sm">{item.subInfo}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-spacing" data-testid="contact-form-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#264653] mb-2 font-['Outfit']">
                Mesaj Gönderin
              </h2>
              <p className="text-[#264653]/70 mb-8">
                Formu doldurun, en kısa sürede size dönüş yapalım.
              </p>

              {submitted ? (
                <div className="text-center p-12 rounded-3xl bg-[#2A9D8F]/5 border border-[#2A9D8F]/10">
                  <div className="w-16 h-16 mx-auto bg-[#2A9D8F]/10 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="text-[#2A9D8F]" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#264653] mb-4 font-['Outfit']">
                    Mesajınız Alındı!
                  </h3>
                  <p className="text-[#264653]/70 mb-6">
                    En kısa sürede sizinle iletişime geçeceğiz.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-primary"
                  >
                    Yeni Mesaj Gönder
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="Adınız Soyadınız"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="ornek@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="0 5XX XXX XX XX"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">
                        Firma Adı
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Firma Adınız"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#264653] mb-2">
                      Mesajınız *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="form-textarea"
                      placeholder="Mesajınızı yazın..."
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <>
                        Mesaj Gönder <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Map & Additional Info */}
            <div>
              <div className="rounded-3xl overflow-hidden h-[400px] mb-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3006.123456789!2d29.08!3d41.11!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sKavac%C4%B1k%20Mah.%20Beykoz%20%C4%B0stanbul!5e0!3m2!1str!2str!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="BDHA Danışmanlık Konum"
                ></iframe>
              </div>

              <div className="p-6 rounded-2xl bg-[#264653]">
                <h3 className="text-xl font-semibold text-white mb-4 font-['Outfit']">
                  Hızlı İletişim
                </h3>
                <p className="text-white/80 mb-6">
                  Acil konular için doğrudan bizi arayabilirsiniz. İş saatleri dışında 
                  da size yardımcı olmaktan memnuniyet duyarız.
                </p>
                <a
                  href="tel:02169989030"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#2A9D8F] text-white font-medium hover:bg-[#2A9D8F]/90 transition-colors"
                >
                  <Phone size={18} /> 0216 998 90 30
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing bg-[#FDFBF7]" data-testid="faq-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <span className="text-[#2A9D8F] font-medium text-sm uppercase tracking-wider">
              SSS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#264653] mt-4 font-['Outfit']">
              Sıkça Sorulan Sorular
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: 'Personel temini süreci ne kadar sürer?',
                a: 'İhtiyacınıza göre günlük, dönemsel veya aylık personel temini yapabiliyoruz. Acil ihtiyaçlar için 24-48 saat içinde personel temin edilebilir.'
              },
              {
                q: 'Hangi sektörlere hizmet veriyorsunuz?',
                a: 'Üretim, lojistik, gıda, tekstil, perakende ve daha birçok sektörde mavi yaka ve beyaz yaka personel temini hizmeti sunuyoruz.'
              },
              {
                q: 'Bordrolama hizmetinize neler dahil?',
                a: 'SGK bildirimleri, ücret bordrosu hazırlama, özlük dosyası yönetimi, yasal uyumluluk takibi ve tüm idari işlemler hizmetimize dahildir.'
              },
              {
                q: 'İSG danışmanlığı zorunlu mu?',
                a: '6331 sayılı kanun gereği, 50 ve üzeri çalışanı olan işletmelerde İSG uzmanı bulundurma zorunluluğu vardır. Daha az çalışanlı işletmeler için de risk değerlendirmesi zorunludur.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white border border-[#264653]/5"
              >
                <h3 className="font-semibold text-[#264653] mb-2 font-['Outfit']">{faq.q}</h3>
                <p className="text-[#264653]/70">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
